import { ApiError } from '../api/client';

/** Default lead time before access expiry when the guest is warned. */
export const GUEST_EXPIRY_WARNING_MS = 5 * 60 * 1000;

/** Delay before retrying a gate refresh that failed due to rate limiting. */
export const GUEST_EXPIRY_TRANSIENT_RETRY_MS = 60 * 1000;

const TRANSIENT_GATE_CODES = new Set(['rate_limited', 'verify_locked_out']);

export type GuestExpirySnapshot = {
  expired: boolean;
  revoked: boolean;
  expiresAt?: string | null;
};

export type GuestAccessExpiryHandle = {
  dispose: () => void;
};

export type GuestAccessExpiryOptions = {
  expiresAt: string | null | undefined;
  warningMs?: number;
  /** Injectable clock for tests (epoch ms). */
  now?: () => number;
  /**
   * Injectable scheduler for tests. Returns a clear function.
   * Default uses `setTimeout`.
   */
  schedule?: (fn: () => void, delayMs: number) => () => void;
  /** Delay after a transient gate failure before retrying (default 60s). */
  transientRetryMs?: number;
  refreshExpiry: () => Promise<GuestExpirySnapshot>;
  onWarning: (remainingMs: number) => void;
  onWarningClear?: () => void;
  /** Optional notice when gate refresh is rate-limited (no logout). */
  onTransientRefreshError?: (code: string) => void;
  onExpiredLogout: () => void | Promise<void>;
};

function parseExpiresAt(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function transientGateCode(cause: unknown): string | null {
  if (cause instanceof ApiError && TRANSIENT_GATE_CODES.has(cause.code)) {
    return cause.code;
  }
  return null;
}

/**
 * Warns in the last few minutes before guest access expiry, then re-checks the
 * gate. If an admin extended access, reschedules; otherwise logs the guest out.
 */
export function startGuestAccessExpiryMonitor(
  options: GuestAccessExpiryOptions,
): GuestAccessExpiryHandle {
  const warningMs = options.warningMs ?? GUEST_EXPIRY_WARNING_MS;
  const transientRetryMs = options.transientRetryMs ?? GUEST_EXPIRY_TRANSIENT_RETRY_MS;
  const now = options.now ?? (() => Date.now());
  const schedule =
    options.schedule ??
    ((fn, delayMs) => {
      const id = window.setTimeout(fn, Math.max(0, delayMs));
      return () => window.clearTimeout(id);
    });

  let disposed = false;
  let clearWarning: (() => void) | null = null;
  let clearExpiry: (() => void) | null = null;
  let warningShown = false;
  let checking = false;

  const clearTimers = () => {
    clearWarning?.();
    clearWarning = null;
    clearExpiry?.();
    clearExpiry = null;
  };

  const arm = (expiresAt: string | null | undefined) => {
    clearTimers();
    if (disposed) return;

    const expiresMs = parseExpiresAt(expiresAt);
    if (expiresMs === null) {
      if (warningShown) {
        warningShown = false;
        options.onWarningClear?.();
      }
      return;
    }

    const remaining = expiresMs - now();
    if (remaining <= 0) {
      void runExpiryCheck();
      return;
    }

    if (remaining <= warningMs) {
      if (!warningShown) {
        warningShown = true;
        options.onWarning(remaining);
      }
    } else {
      if (warningShown) {
        warningShown = false;
        options.onWarningClear?.();
      }
      clearWarning = schedule(() => {
        clearWarning = null;
        if (disposed) return;
        warningShown = true;
        options.onWarning(Math.max(0, expiresMs - now()));
      }, remaining - warningMs);
    }

    clearExpiry = schedule(() => {
      clearExpiry = null;
      void runExpiryCheck();
    }, remaining);
  };

  const scheduleTransientRetry = (code: string) => {
    options.onTransientRefreshError?.(code);
    clearExpiry?.();
    clearExpiry = schedule(() => {
      clearExpiry = null;
      void runExpiryCheck();
    }, transientRetryMs);
  };

  const runExpiryCheck = async () => {
    if (disposed || checking) return;
    checking = true;
    try {
      const snapshot = await options.refreshExpiry();
      if (disposed) return;

      if (snapshot.revoked || snapshot.expired) {
        if (warningShown) {
          warningShown = false;
          options.onWarningClear?.();
        }
        await options.onExpiredLogout();
        return;
      }

      const nextExpiresMs = parseExpiresAt(snapshot.expiresAt);
      // Admin cleared expiry — no further countdown.
      if (nextExpiresMs === null) {
        if (warningShown) {
          warningShown = false;
          options.onWarningClear?.();
        }
        return;
      }

      // Admin extended (or clock skew): reschedule from the fresh timestamp.
      if (nextExpiresMs > now()) {
        arm(snapshot.expiresAt);
        return;
      }

      if (warningShown) {
        warningShown = false;
        options.onWarningClear?.();
      }
      await options.onExpiredLogout();
    } catch (cause) {
      if (disposed) return;
      const transient = transientGateCode(cause);
      if (transient) {
        // Rate limits must not look like access expiry — retry without logout.
        scheduleTransientRetry(transient);
        return;
      }

      // Gate refresh failed — treat as expired to avoid lingering access.
      if (warningShown) {
        warningShown = false;
        options.onWarningClear?.();
      }
      await options.onExpiredLogout();
    } finally {
      checking = false;
    }
  };

  arm(options.expiresAt);

  return {
    dispose: () => {
      if (disposed) return;
      disposed = true;
      clearTimers();
      if (warningShown) {
        warningShown = false;
        options.onWarningClear?.();
      }
    },
  };
}
